import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { reviews } from '../data/storeData';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const ReviewsAndFAQ = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    { q: "How long does shipping take?", a: "Standard shipping takes 3-5 business days domestically. International orders typically arrive within 10-14 days. You will receive a tracking number once dispatched." },
    { q: "What is your return policy?", a: "Unopened tea and unused teaware can be returned within 30 days of purchase for a full refund. We want you to be completely satisfied with your experience." },
    { q: "Are your teas ethically sourced?", a: "Yes, we work directly with small estate farmers ensuring fair compensation, sustainable harvesting practices, and a commitment to preserving traditional methods." }
  ];

  return (
    <section className="bg-plum-dark text-moon-white py-24 md:py-32 px-6" aria-labelledby="reviews-faq-heading">
      <h2 id="reviews-faq-heading" className="sr-only">Reviews and Frequently Asked Questions</h2>
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-16">
        
        {/* Reviews */}
        <div className="flex flex-col justify-center">
          <div className="mb-12">
            <span className="text-jade-light uppercase tracking-[0.3em] text-[10px] font-bold mb-4 block" aria-hidden="true">Testimonials</span>
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-serif">Voices of our Patrons</h3>
          </div>
          <div className="space-y-6">
            {reviews.map((review, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                className="bg-white/5 p-8 rounded-sm border border-white/10 hover:border-white/20 transition-colors backdrop-blur-sm"
              >
                <div className="flex text-jade-light mb-4" aria-label="5 out of 5 stars">
                  {[...Array(5)].map((_, idx) => <SafeIcon key={idx} icon={FiIcons.FiStar} className="fill-current w-4 h-4 mr-1" />)}
                </div>
                <blockquote className="text-gray-300 font-serif text-lg md:text-xl mb-6 leading-relaxed">"{review.text}"</blockquote>
                <cite className="text-xs uppercase tracking-[0.2em] text-jade-light font-medium not-italic">— {review.name}</cite>
              </motion.div>
            ))}
          </div>
        </div>

        {/* FAQ & Shipping */}
        <div className="flex flex-col justify-center lg:pl-12">
          <div className="mb-12">
            <span className="text-jade-light uppercase tracking-[0.3em] text-[10px] font-bold mb-4 block" aria-hidden="true">Information</span>
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-serif">Shipping & Inquiries</h3>
          </div>
          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <div key={i} className="border-b border-white/10">
                <button 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex justify-between items-center text-left py-6 hover:text-jade-light transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jade-light rounded-sm px-2 -mx-2"
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-answer-${i}`}
                >
                  <span className="font-serif text-lg md:text-xl pr-8">{faq.q}</span>
                  <span className="p-2 rounded-full bg-white/5 group-hover:bg-white/10 transition-colors" aria-hidden="true">
                    <SafeIcon icon={openFaq === i ? FiIcons.FiMinus : FiIcons.FiPlus} className="w-4 h-4" />
                  </span>
                </button>
                <AnimatePresence>
                  {openFaq === i && (
                    <motion.div 
                      id={`faq-answer-${i}`}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <p className="text-gray-400 text-sm md:text-base pb-6 leading-relaxed pr-8 px-2">
                        {faq.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default ReviewsAndFAQ;